import {
  getGetBackProcedimentiPraticaIdSoggettiQueryKey,
  useDeleteBackProcedimentiPraticaIdSoggettiId,
  useGetBackProcedimentiPraticaIdSoggetti,
} from '@framework/api-client/api/backoffice/procedimenti/procedimenti.ts';
import { EnumPersonaFisicaDTORole } from '@framework/api-client/api/inquiries-compile/schemas/enumPersonaFisicaDTORole.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { Alert, message } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { DossierPermissionDrawer } from './DossierPermissionDrawer.tsx';
import { DossierPermissionTable } from './dossier-permission/DossierPermissionTable.tsx';

const roleOrder: EnumPersonaFisicaDTORole[] = [
  EnumPersonaFisicaDTORole.TITOLARE,
  EnumPersonaFisicaDTORole.EDITOR,
  EnumPersonaFisicaDTORole.VIEWER,
];

interface IDossierPermissionsTabProps {
  id: string;
  /**
   * Se true, l'utente può vedere la lista dei soggetti, ma non può modificarla.
   */
  readonly: boolean;
}

export function DossierPermissionsTab(props: IDossierPermissionsTabProps) {
  const { id, readonly } = props;

  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const result = useGetBackProcedimentiPraticaIdSoggetti(
    id ?? '<unknown>',
    { includiEliminati: false },
    { query: { enabled: !!id } },
  );

  const subjects = useMemo(() => {
    if (!result.data) return [];
    // Ordino i soggetti per ruolo, cognome e nome
    return result.data.sort((a, b) => {
      const byRole = roleOrder.indexOf(a.ruolo!) - roleOrder.indexOf(b.ruolo!);
      if (byRole !== 0) return byRole;
      const bySurname = (a.cognome ?? '').localeCompare(b.cognome ?? '');
      if (bySurname !== 0) return bySurname;
      return (a.nome ?? '').localeCompare(b.nome ?? '');
    });
  }, [result.data]);

  const remove = useDeleteBackProcedimentiPraticaIdSoggettiId();

  const onRemove = useCallback(
    async (subjectId: string | null | undefined) => {
      if (readonly) return;
      if (!id || !subjectId) return;
      try {
        await remove.mutateAsync({
          idIstanzaPratica: id,
          idSoggettoPratica: subjectId,
        });
        await queryClient.invalidateQueries(
          getGetBackProcedimentiPraticaIdSoggettiQueryKey(id),
        );
        message.success(
          t(
            'dossier-permissions.remove.success',
            'Permesso rimosso con successo',
          ),
        );
      } catch (error) {
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'dossier-permissions.remove.error',
              "Si è verificato un errore durante l'eliminazione del soggetto",
            ),
          ),
        );
      }
    },
    [id, queryClient, readonly, remove, t],
  );

  if (result.isError) {
    return (
      <Alert
        showIcon
        message="Errore lista soggetti"
        description={getNetworkErrorMessage(
          result.error,
          t(
            'dossier-permissions.get-list.generic-error',
            'Si è verificato un errore. Riprovare.',
          ),
        )}
        type="error"
      />
    );
  }

  return (
    <>
      <DossierPermissionTable
        data={subjects}
        onRemove={onRemove}
        loading={result.isLoading}
        readonly={readonly}
      />
      {/* Drawer con pulante di apertura per aggiungere un nuovo soggetto */}
      {!readonly && (
        <>
          <VerticalSpace size={16} />
          <DossierPermissionDrawer dossierId={id} />
        </>
      )}
    </>
  );
}
